/**
 * The cross-book activity feed, LIVE.
 *
 * Extracted out of WeddingOverviewPage and given the polling idiom from
 * communications-v2/operate/LiveActivityStreamPage.tsx wholesale, because that
 * page had already paid for every mistake this one would otherwise repeat:
 *
 *   · POLL_MS = 5000 with a `pausedRef` mirroring the paused state, so the
 *     interval callback reads the CURRENT value rather than the one it closed
 *     over when it was created;
 *   · a monotonic `reqSeq` and a `stale()` guard, because with a five-second
 *     poll a slow response can land after a newer one and paint an older feed
 *     — and an older "as of" stamp — on top of it;
 *   · a `mountedRef` so nothing sets state after unmount;
 *   · a SEPARATE one-second interval whose only job is to re-render, so the
 *     relative timestamps stay honest between polls (and while paused).
 *
 * WHAT IS NOT POLLED IS AS DELIBERATE AS WHAT IS. The KPI tiles, the funnels
 * and the template cards keep their single fetch: they are windowed aggregates
 * an operator reads once, and re-running eighteen of them every five seconds to
 * animate a number nobody is watching would be a self-inflicted load test. This
 * card polls GET /activity, which is one small query.
 *
 * IT IS ALSO NOT SCOPED TO THE DATE FILTER, and that is the one thing this card
 * could most easily lie about — it sits under a page whose every other number
 * obeys the pill. The honesty caption is the SERVER's `note` string rather than
 * copy written here, so the sentence cannot drift away from the query that
 * produced the rows.
 *
 * SEEDED, NOT BLANK: the first paint uses the rows /overview already embedded,
 * so nothing flickers in from empty.
 *
 * ZERO HEX. The live dot and every event dot is a MUI palette key.
 */

import React from 'react';
import {
  Box, Button, Card, CardContent, Divider, Link, Stack, Tooltip, Typography, useTheme,
} from '@mui/material';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { useNavigate } from 'react-router-dom';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type { ActivityEvent } from '../weddingApi';
import { activityLabel, activityTone, relativeTime } from '../weddingFormat';

const POLL_MS = 5000;
const LIMIT = 20;

interface Props {
  /** The rows /overview already fetched, so the first paint is unchanged. */
  seed?: ActivityEvent[] | null;
}

/** The exact instant behind a relative timestamp. Never throws on a bad value. */
function exactInstant(value: string | null | undefined): string {
  if (!value) return 'Unknown time';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'Unknown time' : d.toISOString();
}

/** "as of 15:42:17" — the local wall clock of the last SUCCESSFUL response. */
function asOf(when: Date | null): string | null {
  if (!when) return null;
  try {
    return when.toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch {
    return null;
  }
}

const RecentActivityCard: React.FC<Props> = ({ seed }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Seeded ONCE, from the payload the page already had. Later renders must not
  // clobber live rows with the stale seed of a re-fetched overview.
  const [events, setEvents] = React.useState<ActivityEvent[]>(() => seed || []);
  const [note, setNote] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [paused, setPaused] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState<Date | null>(null);
  // Re-render trigger so relative timestamps stay fresh even while paused.
  const [, setTick] = React.useState(0);

  const mountedRef = React.useRef(true);
  const pausedRef = React.useRef(paused);
  pausedRef.current = paused;
  // Monotonic request id. mountedRef only protects against unmount — with a 5s
  // poll a slow response could still land AFTER a newer one and repaint it.
  const reqSeq = React.useRef(0);

  const fetchFeed = React.useCallback(async () => {
    const seq = reqSeq.current + 1;
    reqSeq.current = seq;
    const stale = () => !mountedRef.current || reqSeq.current !== seq;
    try {
      const res = await weddingApi.getActivity(LIMIT);
      if (stale()) return;
      setEvents(res.events || []);
      setNote(res.note || null);
      setError(null);
      setLastUpdated(new Date());
    } catch (e) {
      if (stale()) return;
      setError(e instanceof WeddingApiError ? e.message : 'The activity feed could not be refreshed.');
    }
  }, []);

  React.useEffect(() => {
    mountedRef.current = true;
    fetchFeed();
    const id = setInterval(() => {
      if (!pausedRef.current) fetchFeed();
    }, POLL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [fetchFeed]);

  // Keep relative timestamps fresh once per second. Separate from the poll on
  // purpose: "3 minutes ago" must become "4 minutes ago" without a request.
  React.useEffect(() => {
    const id = setInterval(() => {
      if (mountedRef.current) setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const stamp = asOf(lastUpdated);
  const liveColor = paused ? 'warning.main' : 'success.main';

  return (
    <Card sx={{ borderRadius: '16px' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Recent activity across every book
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              aria-hidden
              sx={{
                width: 8, height: 8, borderRadius: '50%', flex: 'none',
                backgroundColor: liveColor,
              }}
            />
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: liveColor }}
            >
              {paused ? 'Paused' : 'Live'}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={paused ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
            onClick={() => {
              const next = !paused;
              setPaused(next);
              // Resuming refreshes immediately rather than waiting out the
              // interval: a five-second-old feed behind a button labelled
              // "Live" is the state this control exists to avoid.
              if (!next) fetchFeed();
            }}
          >
            {paused ? 'Resume' : 'Pause'}
          </Button>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          alignItems="baseline"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          {note && (
            <Typography variant="body2" color="text.secondary">
              {note}
            </Typography>
          )}
          {stamp && (
            <Typography variant="caption" color="text.disabled" data-testid="activity-as-of">
              as of {stamp}
            </Typography>
          )}
        </Stack>

        {error && (
          <Typography variant="body2" color="error.main" sx={{ mb: 2 }}>
            {error} Showing the last rows that did arrive.
          </Typography>
        )}

        {events.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing has happened yet.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} spacing={1}>
            {events.map((event) => (
              <Box
                key={event.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  py: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  {/*
                    The dot is a PALETTE KEY resolved from the live theme —
                    money green, contribution teal, sends gold, conversion
                    burgundy — so dark mode and any future palette change follow
                    without a hex to chase.
                  */}
                  <Box
                    aria-hidden
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      mt: 0.75,
                      flex: 'none',
                      backgroundColor: theme.palette[activityTone(event.event_type)].main,
                    }}
                  />
                  <Box>
                    <Typography variant="body2">
                      {activityLabel(event.event_type, event.actor_name)}
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      underline="hover"
                      onClick={() => navigate(`/dashboard/wedding/books/${event.book_id}`)}
                    >
                      {event.wedding_code} · {event.partner1_name} &amp; {event.partner2_name}
                    </Link>
                  </Box>
                </Box>
                <Tooltip title={exactInstant(event.created_at)}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {relativeTime(event.created_at)}
                  </Typography>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentActivityCard;
